import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { AppQueryProvider } from './query/AppQueryProvider';
import reportWebVitals from './reportWebVitals';
import { initializePageZoom } from './utils/pageZoom';
import { applyAppDocumentTitle } from './utils/appDocumentTitle';
import { applyBrowserTranslateGuard } from './utils/browserTranslateGuard';
import {
  initProgramWorkspaceStorage,
  registerProgramWorkspaceRepositoryPageHideFlush,
} from './utils/programEditWorkspace';
import { flushDeviceCacheStorage, initDeviceCacheStorage } from './utils/deviceCacheStorage';

applyBrowserTranslateGuard();
initializePageZoom();
applyAppDocumentTitle();

// CRA/webpack-dev-server 오버레이가 브라우저의 무해한 ResizeObserver 알림을 런타임 오류로 띄우는 것을 막는다.
// (public/index.html 조기 스크립트와 동일 목적 — 번들 이후에도 유지)
function isBenignResizeObserverMessage(message: string): boolean {
  return (
    message.includes('ResizeObserver loop completed with undelivered notifications') ||
    message.includes('ResizeObserver loop limit exceeded')
  );
}

function messageFromUnknown(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'message' in value) {
    return String((value as { message?: unknown }).message ?? '');
  }
  return String(value ?? '');
}

window.addEventListener(
  'error',
  (event) => {
    const message = String(
      event.message || messageFromUnknown(event.error) || ''
    );
    if (!isBenignResizeObserverMessage(message)) return;
    event.stopImmediatePropagation();
    event.preventDefault();
  },
  true
);

const _origConsoleError = console.error.bind(console);
console.error = (...args: unknown[]) => {
  const msg = args.map(messageFromUnknown).join(' ');
  if (isBenignResizeObserverMessage(msg)) return;
  _origConsoleError(...args);
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

registerProgramWorkspaceRepositoryPageHideFlush();
window.addEventListener('pagehide', () => {
  void flushDeviceCacheStorage();
});

void Promise.all([initProgramWorkspaceStorage(), initDeviceCacheStorage()]).then(() => {
  root.render(
    <React.StrictMode>
      <AppQueryProvider>
        <App />
      </AppQueryProvider>
    </React.StrictMode>
  );
});

reportWebVitals();
